import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useResetRecoilState } from 'recoil';

import { EnquiryType } from '@shared/api/enquiry/enquiry.interface';
import { useFuneralHomeInfoListHook } from '@shared/api/funeralHomeInfo/funeralHomeInfo.hook';
import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { FuneralHomeInfoListStateSelector } from '@shared/api/funeralHomeInfo/funeralHomeInfo.selector';
import { excludeClosedFuneralHomes } from '@shared/api/funeralHomeInfo/funeralHomeInfo.util';
import { Button } from '@shared/components/Button';
import { FileUploader } from '@shared/components/FileUploader';
import { Icon } from '@shared/components/icons';
import { LoadingModal } from '@shared/components/LoadingModal';
import Map from '@shared/components/Map';
import TelField from '@shared/components/TelField';
import { TextField } from '@shared/components/TextField';
import { Coord } from '@shared/interfaces/map.interface';
import { customThemeAtom } from '@shared/state/atom/theme.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { checkFilesSize } from '@shared/utils/checkFilesSize';
import { classNames } from '@shared/utils/joinClassNamesUtils';
import { mainColorGenerator } from '@shared/utils/mainColorGenerator';
import FuzzySearch from 'fuzzy-search';
import { useForm, useWatch } from 'react-hook-form';

import AddressSearcher, { SEARCH_TEXT_ID } from '../../AddressSearcher/AddressSearcher';
import { AddressMap } from '../../FuneralHomeInfoEditor/FuneralHomeAddressPreview';
import {
  formatCoordMove,
  hasCoordValue,
} from '../../FuneralHomeInfoEditor/funeralHomeAddressChange.util';
import FuneralHomeInfoSearchList from '../../FuneralHomeInfoSearcher/FuneralHomeInfoSearchList';
import { searchTextAtomFamily } from '../../SearchBar/state/searchBar.atom';
import {
  ENQUIRY_FILE_TOO_LARGE_MESSAGE,
  ENQUIRY_FILES_SIZE_LIMIT,
  useCreateEnquiry,
  useSeedEnquiryContact,
} from '../useCreateEnquiry';
import {
  buildFuneralHomeEnquiry,
  FUNERAL_HOME_ENQUIRY_FIELD_LABEL,
  FUNERAL_HOME_ENQUIRY_FIELDS,
  formatMapLocation,
  FuneralHomeEnquiryField,
  FuneralHomeEnquiryFields,
  FuneralHomeEnquiryMode,
  hasFuneralHomeChanges,
  isSameMapLocation,
} from './buildFuneralHomeEnquiry';

const SEARCH_PAGE_SIZE = 5;

type FuneralHomeItem = FuneralHomeInfo<undefined, string>;

interface FormValues {
  name: string;
  tel: string;
  fields: Record<FuneralHomeEnquiryField, string>;
  memo: string;
  files: FileList;
}

const EMPTY_FIELDS: Record<FuneralHomeEnquiryField, string> = {
  name: '',
  address: '',
  mapLocation: '',
  phoneNumber: '',
  parkingAddress: '',
  additionalInfo: '',
};

//* 여러 줄 입력이 자연스러운 항목
const MULTILINE_FIELDS: ReadonlySet<FuneralHomeEnquiryField> =
  new Set<FuneralHomeEnquiryField>(['mapLocation', 'parkingAddress', 'additionalInfo']);

const FIELD_PLACEHOLDER: Record<FuneralHomeEnquiryField, string> = {
  name: '장례식장 이름',
  address: '주소',
  mapLocation: '예: 핀이 길 건너편에 찍혀요. 정문은 OO 옆이에요',
  phoneNumber: '전화번호',
  parkingAddress: '예: 지하 1층 주차장 이용',
  additionalInfo: '예: 주차권은 1층 안내데스크에서 받으세요',
};

export interface FuneralHomeEnquiryFormProps {
  type: EnquiryType;
  onSuccess: () => void;
  initialMode?: FuneralHomeEnquiryMode;
}

/**
 * 장례식장 추가·수정 요청 양식 — 기존 문의(createEnquiry)로 접수된다. 백엔드·새 API 없음.
 * - 수정: 장례식장 검색 → 선택 → 바꿀 항목 체크 → 입력
 * - 추가: 이름·주소 필수, 나머지 선택. 수정에서 검색 결과가 없으면 검색어를 이름에 채워 추가로 넘어온다
 * 제목·내용 조립 규칙은 buildFuneralHomeEnquiry.
 */
export const FuneralHomeEnquiryForm: FC<FuneralHomeEnquiryFormProps> = ({
  type,
  onSuccess,
  initialMode = 'edit',
}) => {
  const { userProfile } = useAuth();
  const customTheme = useRecoilValue(customThemeAtom);
  const { CheckBox: CheckMark } = Icon();
  const { submit, isLoading } = useCreateEnquiry(type);

  const [mode, setMode] = useState<FuneralHomeEnquiryMode>(initialMode);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, control, setValue, getValues } = useForm<FormValues>({
    defaultValues: { name: '', tel: '', fields: EMPTY_FIELDS, memo: '' },
  });
  const telValue = useWatch({ control, name: 'tel', defaultValue: '' });
  const addressValue = useWatch({ control, name: 'fields.address', defaultValue: '' });

  //* 성함·연락처는 1:1 문의 폼과 같은 규칙으로 1회 시딩 (refetch 덮어쓰기 방지)
  useSeedEnquiryContact(userProfile, ({ name, phone }) => {
    if (name && !getValues('name')) setValue('name', name);
    if (phone && !getValues('tel')) setValue('tel', phone);
  });

  //* ---- 수정: 장례식장 검색·선택 ----
  useFuneralHomeInfoListHook();
  const listState = useRecoilValue(FuneralHomeInfoListStateSelector);
  const [searchValue, setSearchValue] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);
  const [selected, setSelected] = useState<FuneralHomeItem | null>(null);
  const [checked, setChecked] = useState<Set<FuneralHomeEnquiryField>>(() => new Set());
  //* onMapLocationChange는 참조를 고정해야 해서(AddressSearcher effect) 고른 장례식장은 ref로 본다
  const selectedRef = useRef<FuneralHomeItem | null>(null);
  selectedRef.current = selected;

  //* ---- 지도 위치: 검색으로 고른 자리(변경 후) ----
  const [mapPick, setMapPick] = useState<{ address: string; coord: Coord } | null>(null);
  const [mapHint, setMapHint] = useState<string | null>(null);
  //* 검색어가 recoil에 남으면 같은 검색어로 다시 찾을 때 목록이 안 열린다 → 입력칸째 비우고 다시 마운트
  //* (장례식장 수정 화면 FuneralHomeInfoEditor와 같은 처리)
  const resetSearchText = useResetRecoilState(searchTextAtomFamily(SEARCH_TEXT_ID));
  const [searcherKey, setSearcherKey] = useState(0);
  const clearMapPick = () => {
    setMapPick(null);
    setMapHint(null);
    resetSearchText();
    setSearcherKey((key) => key + 1);
  };

  const searched = useMemo<FuneralHomeItem[]>(() => {
    if (!searchValue.trim()) return [];
    if (listState.status !== 'success' || !listState.data) return [];
    return new FuzzySearch(excludeClosedFuneralHomes(listState.data), [
      'name',
      'address',
    ]).search(searchValue.trim());
  }, [listState, searchValue]);

  const selectHome = (item: FuneralHomeItem) => {
    setSelected(item);
    setChecked(new Set());
    //* 현재 값을 미리 채워 두어 일부만 고칠 수 있게 한다
    setValue('fields', {
      name: item.name ?? '',
      address: item.address ?? '',
      mapLocation: '',
      phoneNumber: item.phoneNumber ?? '',
      parkingAddress: item.parkingAddress ?? '',
      additionalInfo: '',
    });
    clearMapPick();
    setError(null);
  };

  //* 선택 해제·모드 전환 공통: 선택·체크·입력칸·지도 찍기 초기화
  const resetFields = (fields: typeof EMPTY_FIELDS) => {
    setSelected(null);
    setChecked(new Set());
    setValue('fields', fields);
    clearMapPick();
  };

  const clearSelected = () => resetFields(EMPTY_FIELDS);

  const toggleField = (field: FuneralHomeEnquiryField, on: boolean) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(field);
      else next.delete(field);
      return next;
    });
  };

  //* 검색 결과가 없을 때: 검색어를 이름에 채워 추가 요청으로
  const switchToAdd = (presetName?: string) => {
    setMode('add');
    resetFields({ ...EMPTY_FIELDS, name: (presetName ?? '').trim() });
    setError(null);
  };

  const switchToEdit = () => {
    setMode('edit');
    resetFields(EMPTY_FIELDS);
    setError(null);
  };

  //* ---- 추가: 주소 검색 (직접 입력도 허용) ----
  //* AddressSearcher는 onChange 참조가 바뀌면 effect가 다시 돌므로 고정한다.
  //* 빈 주소(초기화 신호)는 무시 — 직접 입력한 주소가 지워지지 않게
  const onAddressChange = useCallback(
    (item: { address: string; coord?: Coord }) => {
      if (!item.address) return;
      setValue('fields.address', item.address);
      //* 검색으로 골랐으면 좌표까지 — 운영팀이 장례식장을 만들 때 그대로 쓴다 (빈 값·0 좌표는 제외)
      if (hasCoordValue(item.coord))
        setValue('fields.mapLocation', formatMapLocation(item.address, item.coord));
    },
    [setValue],
  );
  const mapLocationValue = useWatch({
    control,
    name: 'fields.mapLocation',
    defaultValue: '',
  });

  //* ---- 수정: 지도 위치 ---- 주소는 맞는데 핀이 다른 곳에 찍히는 경우.
  //* 검색으로 맞는 자리를 고르면 좌표가 들어가고, 못 찾으면 글로 설명한다
  //* 현재 좌표와 10m 안쪽이면 같은 자리로 보고 받지 않는다 (장례식장 수정 화면과 같은 기준).
  //* 접수 줄에는 이동 거리를 함께 적어 운영팀이 얼마나 옮기는 요청인지 바로 보게 한다
  const onMapLocationChange = useCallback(
    (item: { address: string; coord?: Coord }) => {
      if (!item.address) return;
      if (!hasCoordValue(item.coord)) {
        setValue('fields.mapLocation', item.address);
        return;
      }
      const before = selectedRef.current?.coord;
      if (isSameMapLocation(before, item.coord)) {
        setMapPick(null);
        setMapHint(
          '고른 자리가 현재 등록된 위치와 같아요 (10m 이내). 다른 자리를 골라 주세요.',
        );
        return;
      }
      setMapHint(null);
      setMapPick({ address: item.address, coord: item.coord });
      setValue(
        'fields.mapLocation',
        formatMapLocation(item.address, item.coord, formatCoordMove(before, item.coord)),
      );
    },
    [setValue],
  );

  const onSubmit = async (data: FormValues) => {
    setError(null);
    const file = data.files?.[0] ?? null;
    if (file && !checkFilesSize(data.files, ENQUIRY_FILES_SIZE_LIMIT)) {
      setError(ENQUIRY_FILE_TOO_LARGE_MESSAGE);
      return;
    }

    let built: { title: string; content: string };
    if (mode === 'edit') {
      if (!selected) {
        setError('수정할 장례식장을 검색해서 골라 주세요.');
        return;
      }
      const changes: FuneralHomeEnquiryFields = {};
      checked.forEach((field) => {
        changes[field] = data.fields[field];
      });
      if (checked.size === 0) {
        setError('바꿀 항목을 하나 이상 체크해 주세요.');
        return;
      }
      if (!hasFuneralHomeChanges(changes)) {
        setError('체크한 항목의 내용을 입력해 주세요.');
        return;
      }
      built = buildFuneralHomeEnquiry({
        mode: 'edit',
        target: { _id: selected._id, name: selected.name },
        changes,
        memo: data.memo,
      });
    } else {
      const name = data.fields.name.trim();
      const address = data.fields.address.trim();
      if (!name || !address) {
        setError('장례식장 이름과 주소는 꼭 입력해 주세요.');
        return;
      }
      built = buildFuneralHomeEnquiry({
        mode: 'add',
        fields: { ...data.fields, name, address },
        memo: data.memo,
      });
    }

    const ok = await submit({
      name: data.name,
      phoneNumber: data.tel,
      title: built.title,
      content: built.content,
      file,
    });
    if (ok) onSuccess();
  };

  const isEditMode = mode === 'edit';
  const modeButtonClass = (active: boolean) =>
    classNames(
      'min-h-[44px] rounded-lg border px-2 py-2.5 text-sm font-bold transition-colors focus-visible:ring-1 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
      active
        ? 'border-gray-900 bg-gray-900 text-white'
        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50',
    );

  const renderFieldInput = (field: FuneralHomeEnquiryField) =>
    MULTILINE_FIELDS.has(field) ? (
      <textarea
        aria-label={FUNERAL_HOME_ENQUIRY_FIELD_LABEL[field]}
        placeholder={FIELD_PLACEHOLDER[field]}
        className="textfield h-24 w-full rounded-lg py-3 text-sm font-normal leading-6"
        {...register(`fields.${field}` as const)}
      />
    ) : (
      <TextField
        type="text"
        aria-label={FUNERAL_HOME_ENQUIRY_FIELD_LABEL[field]}
        placeholder={FIELD_PLACEHOLDER[field]}
        className="w-full rounded-lg text-sm font-normal"
        {...register(`fields.${field}` as const)}
      />
    );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* 수정 / 추가 */}
        <div
          className="grid grid-cols-2 gap-2"
          role="group"
          aria-label="요청 종류"
        >
          <button
            type="button"
            className={modeButtonClass(isEditMode)}
            aria-pressed={isEditMode}
            onClick={switchToEdit}
          >
            기존 장례식장 수정
          </button>
          <button
            type="button"
            className={modeButtonClass(!isEditMode)}
            aria-pressed={!isEditMode}
            onClick={() => switchToAdd()}
          >
            새 장례식장 추가
          </button>
        </div>

        <div className="grid gap-3 border-b border-gray-200 pb-5 sm:grid-cols-2">
          <TextField
            type="text"
            label="성함"
            placeholder="성함을 입력해주세요."
            className="w-full rounded-lg text-sm font-normal"
            labelClassname="pb-1 text-sm font-medium"
            {...register('name', { required: true })}
          />
          <TelField
            label="연락처"
            labelClassname="pb-1 text-sm font-medium"
            value={telValue}
            register={register}
            name="tel"
            className="w-full rounded-lg text-sm font-normal"
            required={true}
          />
        </div>

        {isEditMode ? (
          <>
            {/* 장례식장 검색·선택 */}
            <div>
              <p className="pb-2 text-sm font-medium text-gray-800">장례식장</p>
              {selected ? (
                <div className="space-y-1 rounded-xl border border-gray-200 bg-white p-4 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-bold">{selected.name}</p>
                    <button
                      type="button"
                      className="-mt-2 -mr-2 inline-flex min-h-[44px] shrink-0 items-center rounded-lg px-2 text-xs font-medium text-gray-500 underline underline-offset-2 hover:bg-gray-50 focus-visible:ring-1 focus-visible:ring-gray-400"
                      onClick={clearSelected}
                    >
                      다시 선택
                    </button>
                  </div>
                  <p className="text-gray-600">{selected.address}</p>
                  {selected.phoneNumber && (
                    <p className="text-gray-600">전화 {selected.phoneNumber}</p>
                  )}
                  {selected.parkingAddress && (
                    <p className="text-gray-600">주차 {selected.parkingAddress}</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <TextField
                    type="text"
                    className="w-full rounded-lg text-sm font-normal"
                    placeholder="장례식장 이름 및 지역을 검색하세요."
                    value={searchValue}
                    onChange={(e) => {
                      setSelectedPage(1);
                      setSearchValue(e.target.value);
                    }}
                    //* 검색칸에서 Enter는 폼 제출이 아니다 (고르기 전 "장례식장을 골라 주세요" 오류 방지)
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.preventDefault();
                    }}
                  />
                  {searchValue.trim() &&
                    (listState.status !== 'success' ? (
                      <p className="py-2 text-center text-xs text-gray-500">
                        장례식장 목록을 불러오는 중입니다.
                      </p>
                    ) : searched.length === 0 ? (
                      <button
                        type="button"
                        className="w-full rounded-xl border border-dashed border-gray-300 bg-white px-4 py-4 text-center text-sm"
                        onClick={() => switchToAdd(searchValue)}
                      >
                        <span className="font-bold">찾는 장례식장이 없어요</span>
                        <br />
                        <span className="text-xs text-gray-500">
                          누르면 입력한 이름으로 추가 요청을 작성합니다
                        </span>
                      </button>
                    ) : (
                      <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 text-sm">
                        <FuneralHomeInfoSearchList
                          searchedFuneralHomeInfoList={searched}
                          onItemClick={selectHome}
                          selectedPage={selectedPage}
                          setSelectedPage={setSelectedPage}
                          chunkSize={SEARCH_PAGE_SIZE}
                        />
                        <button
                          type="button"
                          className="text-xs text-gray-500 underline"
                          onClick={() => switchToAdd(searchValue)}
                        >
                          찾는 장례식장이 없나요? 추가 요청
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* 바꿀 항목 */}
            {selected && (
              <div>
                <p className="pb-1 text-sm font-medium text-gray-800">바꿀 항목</p>
                <p className="break-keep pb-3 text-xs leading-5 text-gray-500">
                  체크한 항목만 접수됩니다. 현재 값이 채워져 있으니 고쳐 주세요.
                </p>
                <div className="space-y-2">
                  {FUNERAL_HOME_ENQUIRY_FIELDS.map((field) => {
                    const on = checked.has(field);
                    return (
                      <div
                        key={field}
                        className={classNames(
                          'overflow-hidden rounded-xl border bg-white',
                          on ? 'border-gray-300' : 'border-gray-200',
                        )}
                      >
                        <label className="relative flex min-h-[48px] cursor-pointer items-center gap-3 px-3 py-3 text-sm hover:bg-gray-50">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={on}
                            onChange={(e) => toggleField(field, e.currentTarget.checked)}
                            aria-label={`${FUNERAL_HOME_ENQUIRY_FIELD_LABEL[field]} 변경`}
                            aria-expanded={on}
                            aria-controls={on ? `enquiry-field-${field}` : undefined}
                          />
                          <span
                            aria-hidden="true"
                            className="center-box h-5 w-5 shrink-0 border-2 peer-focus-visible:ring-1 peer-focus-visible:ring-gray-400 peer-focus-visible:ring-offset-2"
                            style={{
                              backgroundColor: on
                                ? mainColorGenerator(customTheme)
                                : '#fff',
                              borderColor: on
                                ? mainColorGenerator(customTheme)
                                : '#d1d5db',
                            }}
                          >
                            {on && <CheckMark />}
                          </span>
                          <span className={on ? 'font-bold' : ''}>
                            {FUNERAL_HOME_ENQUIRY_FIELD_LABEL[field]}
                          </span>
                          <span className="sr-only"> 변경</span>
                        </label>
                        {on && field === 'mapLocation' && (
                          <div
                            id={`enquiry-field-${field}`}
                            className="space-y-4 border-t border-gray-100 px-3 pt-3 pb-4"
                          >
                            {/* 변경 전/후 비교 — 장례식장 수정 화면(FuneralHomeAddressPreview)과 같은 틀 */}
                            <div
                              className={classNames(
                                'grid grid-cols-1 gap-2',
                                mapPick ? 'sm:grid-cols-2' : '',
                              )}
                            >
                              <section className="flex flex-col gap-1 rounded-lg border border-gray-200 p-2">
                                <span className="self-start bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
                                  현재 등록된 위치
                                </span>
                                <AddressMap
                                  coord={selected.coord}
                                  name={selected.name}
                                  emptyText="등록된 좌표 없음"
                                  className="h-36 overflow-hidden rounded-lg"
                                />
                              </section>
                              {mapPick && (
                                <section className="flex flex-col gap-1 rounded-lg border p-2 theme-border-main">
                                  <span className="self-start px-2 py-0.5 text-xs font-medium text-white theme-bg-main">
                                    변경 요청할 위치
                                  </span>
                                  <p className="break-keep text-xs font-bold">
                                    {mapPick.address}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatCoordMove(selected.coord, mapPick.coord)}
                                  </p>
                                  <div className="mt-auto h-36 w-full overflow-hidden rounded-lg">
                                    <Map coord={mapPick.coord} name={mapPick.address} />
                                  </div>
                                </section>
                              )}
                            </div>
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-800">
                                변경할 위치 검색
                              </p>
                              <AddressSearcher
                                key={searcherKey}
                                onChange={onMapLocationChange}
                                placeholder="이름 또는 주소 검색"
                                selectedLocationLabel="변경 요청할 위치"
                                compact
                                hideSelectedPreview
                              />
                              {mapHint && (
                                <p
                                  role="status"
                                  className="break-keep text-xs text-red-600"
                                >
                                  {mapHint}
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <p className="break-keep text-xs leading-5 text-gray-500">
                                검색이 어렵다면 글로 설명해주세요.
                              </p>
                              {renderFieldInput(field)}
                            </div>
                          </div>
                        )}
                        {on && field !== 'mapLocation' && (
                          <div
                            id={`enquiry-field-${field}`}
                            className="border-t border-gray-100 px-3 pt-3 pb-4"
                          >
                            {renderFieldInput(field)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <TextField
              type="text"
              label="장례식장 이름"
              placeholder="장례식장 이름"
              className="w-full rounded-lg text-sm font-normal"
              labelClassname="pb-1 text-sm font-medium"
              {...register('fields.name')}
            />
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-800">주소</p>
              <p className="break-keep pb-1 text-xs leading-5 text-gray-500">
                검색되지 않는 곳(마을회관 등)은 아래 칸에 직접 적어 주세요.
              </p>
              <AddressSearcher
                key={searcherKey}
                onChange={onAddressChange}
                placeholder="장례식장 이름 또는 주소 검색"
                compact
              />
              <TextField
                type="text"
                aria-label="주소 직접 입력"
                placeholder="주소 직접 입력"
                className="w-full rounded-lg text-sm font-normal"
                {...register('fields.address')}
              />
              {addressValue.trim() && (
                <p className="pl-2 text-xs text-gray-500">접수 주소: {addressValue}</p>
              )}
              <input type="hidden" {...register('fields.mapLocation')} />
              {mapLocationValue.trim() && (
                <p className="pl-2 text-xs text-gray-500">
                  지도 위치: {mapLocationValue}
                </p>
              )}
            </div>
            <TextField
              type="text"
              label="전화번호"
              placeholder="전화번호 (선택)"
              className="w-full rounded-lg text-sm font-normal"
              labelClassname="pb-1 text-sm font-medium"
              {...register('fields.phoneNumber')}
            />
            <div>
              <p className="pb-1 text-sm font-medium text-gray-800">주차 안내</p>
              {renderFieldInput('parkingAddress')}
            </div>
            <div>
              <p className="pb-1 text-sm font-medium text-gray-800">추가 안내</p>
              {renderFieldInput('additionalInfo')}
            </div>
          </>
        )}

        {/* 메모 */}
        <div>
          <label
            htmlFor="funeral-home-enquiry-memo"
            className="mb-2 block text-sm font-medium text-gray-800"
          >
            메모 <span className="font-normal text-gray-400">(선택)</span>
          </label>
          <textarea
            id="funeral-home-enquiry-memo"
            placeholder="운영팀에 전할 말이 있으면 적어 주세요. (선택)"
            className="textfield h-24 w-full rounded-lg py-3 text-sm font-normal leading-6"
            {...register('memo')}
          />
        </div>

        {/* 사진 1장 */}
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-1 text-sm">
            <p className="font-medium text-gray-700">
              사진 <span className="font-normal text-gray-400">(선택)</span>
            </p>
            <span className="text-xs text-gray-500">1장 · 최대 50MB</span>
          </div>
          <FileUploader
            className="overflow-hidden rounded-lg"
            placeholder="필요한 이미지를 첨부해주세요"
            accept="image/*"
            {...register('files')}
          />
        </div>

        {error && (
          <p role="alert" className="pl-2 text-sm font-bold text-red-600">
            {error}
          </p>
        )}

        <div className="space-y-3">
          <p className="text-center text-sm text-gray-600">
            확인 후 반영하고 답변을 드립니다.
          </p>
          <Button
            type="submit"
            className="button-rectangle w-full rounded-xl bg-gray-900 py-3 text-base text-white hover:bg-gray-800 focus-visible:ring-1 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
          >
            요청 보내기
          </Button>
        </div>
      </form>
      <LoadingModal open={isLoading} title={'문의 요청중'} />
    </>
  );
};
